<script setup lang="ts">
import DataLoading from '@/components/data-loading/data-loading.vue'
import { useZendeskAuthSync } from '@/composables/useZendeskWidget'
import Navigation from '@/layout/app/navigation.vue'
import { appLoadError, retryRouteLoad, useKeepRouteDataAlive } from '@/lib/appLoad'
import { router } from '@/router'
import { browserFullPathToRouter } from '@/utils/authLoginRedirect'
import { LToastProvider } from '@repo/lui'
import * as Sentry from '@sentry/vue'
import { VueQueryDevtools } from '@tanstack/vue-query-devtools'
import { useHead } from '@unhead/vue'
import { computed, ref } from 'vue'
import { isNavigationFailure, NavigationFailureType } from 'vue-router'

const isDevDomain = window.location.hostname.includes('lykadev.com.au')
const zendeskAllowedHost = import.meta.env.DEV || isDevDomain

const isRouteReady = ref(false)
const bootFailed = ref(false)

const awaitRouteReady = () => {
  router
    .isReady()
    .then(() => {
      isRouteReady.value = true
    })
    .catch((error) => {
      // Expected: auth guard (router.ts) hands off to unified login, redirect
      // already in flight. isReady() rejecting on this abort is intentional
      // vue-router behaviour, not a bug: https://github.com/vuejs/router/issues/1305
      // fix: https://github.com/vuejs/router/commit/509fc0d5c78b32cbd5ce0c86f716774ad6163cad
      //
      // Also swallow `cancelled`: a second navigation starting while the first
      // guard chain is still awaiting (e.g. browser back/forward mid-boot) is
      // the same benign race, just reported under a different failure type.
      if (isNavigationFailure(error, NavigationFailureType.aborted | NavigationFailureType.cancelled)) return

      const failure = new Error('Failed to complete initial navigation on app startup', { cause: error })

      Sentry.captureException(failure)

      appLoadError.value = failure
      // DataLoading shows its loading slot ahead of its error slot (loading then error, in that order),
      // so startup must be marked "done" (with an error) rather than left `loading`, or the spinner hides the error/retry UI forever.
      isRouteReady.value = true
      bootFailed.value = true
    })
}

awaitRouteReady()

const isReady = computed(() => isRouteReady.value && !appLoadError.value)

const onTryAgain = () => {
  // retryRouteLoad can't recover a failed initial navigation (nothing was fetched yet)
  // retry the navigation itself instead, to wherever the browser's URL actually is.
  if (bootFailed.value) {
    appLoadError.value = null
    bootFailed.value = false
    isRouteReady.value = false

    router.replace(browserFullPathToRouter()).catch(() => {})

    awaitRouteReady()

    return
  }

  retryRouteLoad()
}

useKeepRouteDataAlive()
useZendeskAuthSync({ allowedHost: zendeskAllowedHost })

useHead({
  titleTemplate: '%s - Lyka account',
  meta: isDevDomain ? [{ name: 'robots', content: 'noindex, nofollow' }] : [],
})
</script>

<template>
  <LToastProvider position="bottom-center" />

  <div class="flex min-h-dvh flex-col-reverse md:flex-col">
    <Navigation />

    <main class="flex grow flex-col">
      <DataLoading
        v-if="!isReady"
        :loading="!isRouteReady"
        :error="appLoadError"
        full-page
        variant="spinner"
        @try-again="onTryAgain"
      />

      <router-view v-else v-slot="{ Component }">
        <component :is="Component" ref="mainContent" />
      </router-view>

      <VueQueryDevtools v-if="false" />
    </main>
  </div>
</template>
